import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { selectAdminToken } from "../../sessionSlice"; // adjust import path as needed
import "./qu-add-question-modal.css";

export default function AddQuestionModal({ isOpen, onClose, questionId }) {
  const [loading, setLoading] = useState(false);
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(Array(3).fill(""));
  const [correctAnswer, setCorrectAnswer] = useState("1");
  const [description, setDescription] = useState("");
  const [explain, setExplain] = useState("");
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data: themeData } = useSelector((state) => state.theme);

  console.log(themeData);

  function fixEncoding(value) {
    return value;
  }

  // Fallback CP1252 decoder for browsers/environments without TextDecoder('windows-1252')
  function cp1252DecodeFallback(bytes) {
    const cp1252Map = {
      0x80: "\u20AC",
      0x81: "\u0081",
      0x82: "\u201A",
      0x83: "\u0192",
      0x84: "\u201E",
      0x85: "\u2026",
      0x86: "\u2020",
      0x87: "\u2021",
      0x88: "\u02C6",
      0x89: "\u2030",
      0x8a: "\u0160",
      0x8b: "\u2039",
      0x8c: "\u0152",
      0x8d: "\u008D",
      0x8e: "\u017D",
      0x8f: "\u008F",
      0x90: "\u0090",
      0x91: "\u2018",
      0x92: "\u2019",
      0x93: "\u201C",
      0x94: "\u201D",
      0x95: "\u2022",
      0x96: "\u2013",
      0x97: "\u2014",
      0x98: "\u02DC",
      0x99: "\u2122",
      0x9a: "\u0161",
      0x9b: "\u203A",
      0x9c: "\u0153",
      0x9d: "\u009D",
      0x9e: "\u017E",
      0x9f: "\u0178",

      // critical mojibake fixes
      0xfd: "\u2019", // ’ (right single quote)
      0xdd: "\u201D", // ” (right double quote)
    };

    let out = "";
    for (let i = 0; i < bytes.length; i++) {
      const b = bytes[i];

      // NEW: direct fix for problematic bytes
      if (b === 0xfd || b === 0xdd) {
        out += cp1252Map[b];
        continue;
      }

      if (b <= 0x7f) out += String.fromCharCode(b);
      else if (b >= 0x80 && b <= 0x9f)
        out += cp1252Map[b] || String.fromCharCode(b);
      else if (b >= 0xa0) out += String.fromCharCode(b);
    }

    return out;
  }

  // Load existing question when editing
  useEffect(() => {
    if (!isOpen) return;
    if (questionId) {
      setLoading(true);
      fetch(`${process.env.REACT_APP_BACKEND_URL}/getQuestionById`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ id: questionId }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.success && data.question) {
            const q = data.question;
            console.log("final question render",q);
            setQuestion(q.question_name || "");
            setOptions([
              q.option_one || "",
              q.option_two || "",
              q.option_three || "",
            ]);
            setCorrectAnswer(String(q.answer_id));
            // NEW: populate description & explain from API if present
            setDescription(q.description || "");
            setExplain(q.explaination || "");
          } else {
            Swal.fire("Error", "Failed to fetch question", "error");
            onClose();
          }
        })
        .catch(() => {
          Swal.fire("Error", "Failed to fetch question", "error");
          onClose();
        })
        .finally(() => setLoading(false));
    } else {
      // Reset for creation
      setQuestion("");
      setOptions(Array(3).fill(""));
      setCorrectAnswer("1");
      setDescription("");
      setExplain("");
    }
  }, [isOpen, questionId]);

  const handleOptionChange = (idx, value) => {
    let newVal = value;
    if (value.length > 150) {
      Swal.fire(
        "Limit Exceeded",
        "Options can be at most 150 characters; extra text has been trimmed.",
        "warning"
      );
      newVal = value.slice(0, 150);
    }
    setOptions((prev) => {
      const newOpts = [...prev];
      newOpts[idx] = newVal;
      return newOpts;
    });
  };


  const handleQuestionChange = (val) => {
    let newVal = val;
    if (val.length > 300) {
      Swal.fire(
        "Limit Exceeded",
        "Question can be at most 300 characters; extra text has been trimmed.",
        "warning"
      );
      newVal = val.slice(0, 300);
    }
    setQuestion(newVal);
  };

  const handleSubmit = async () => {
    if (!question.trim()) {
      Swal.fire("Error", "Question is required", "error");
      return;
    }
    for (let i = 0; i < options.length; i++) {
      const text = options[i]?.trim();
      if (!text) {
        Swal.fire("Error", `Option ${i + 1} is required`, "error");
        return;
      }
    }

    const formData = new FormData();
    if (questionId) formData.append("id", questionId);
    formData.append("question", question);
    const normalizedOptions = options.map((t) => (t ? t.trim() : ""));
    formData.append("options", JSON.stringify(normalizedOptions));
    formData.append("correctAnswer", correctAnswer);
    formData.append("currentTheme", currentTheme);
    formData.append("hintForceRemove", false);
    formData.append("description", description);
    formData.append("explain", explain);
    const endpoint = "/addNewQuestion";

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}${endpoint}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${adminToken}`,
          },
          body: formData,
        }
      );
      if (!res.ok) throw new Error("Request failed");
      Swal.fire(
        "Success",
        questionId ? "Question updated" : "Question added",
        "success"
      );
      window.location.reload();
      onClose();
    } catch (err) {
      Swal.fire("Error", "Failed to save question", "error");
    }
  };

  if (!isOpen) return null;
  return (
    <>
      <div className="qu-modal-backdrop" onClick={onClose} />
      <div className="qu-modal">
        <h2>
          {loading
            ? "Loading..."
            : questionId
            ? "Edit Question"
            : "Add New Question"}
        </h2>

        <div className="qu-form-group">
          <label>Question</label>
          <input
            type="text"
            value={fixEncoding(question)}
            onChange={(e) => handleQuestionChange(e.target.value)}
            placeholder="Enter question"
            disabled={loading}
          />
          <div className="code">* Max length 300 characters.</div>
        </div>

        <div className="qu-form-group-v1">
          {options.map((opt, i) => {
            const letter = String.fromCharCode(65 + i); // A, B, C

            return (
              <div className="input-option-holder" key={i}>
                <div className="input-label">{`Option ${letter}`}</div>
                <input
                  type="text"
                  placeholder={`Option ${letter}`}
                  value={fixEncoding(opt)}
                  disabled={loading}
                  onChange={(e) => handleOptionChange(i, e.target.value)}
                />
              </div>
            );
          })}
        </div>
        <div className="character-limit">* Max length 150 characters per option.</div>

        <div className="qu-form-group">
          <label>Correct Answer</label>
          <div style={{ display: "flex", gap: "15px", marginTop: "6px" }}>
            {options.map((_, i) => {
              const letter = String.fromCharCode(65 + i); // 65 = 'A'
              return (
                <label
                  key={i}
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  <input
                    type="radio"
                    name="correctAnswer"
                    value={String(i + 1)}
                    disabled={loading}
                    checked={correctAnswer === String(i + 1)}
                    onChange={(e) => setCorrectAnswer(e.target.value)}
                  />
                  {letter}
                </label>
              );
            })}
          </div>
        </div>

        <div className="qu-actions">
          <button
            className="qu-cancel-btn"
            disabled={loading}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="qu-save-btn"
            disabled={loading}
            onClick={handleSubmit}
          >
            {questionId ? "Update" : "Save Question"}
          </button>
        </div>
      </div>
    </>
  );
}
